import { useEffect, useState } from "react";
import { X, Save, Loader2 } from "lucide-react";
import { updateChild } from "@/lib/api";
import { CHILDREN } from "@/constants/testIds";
import { toast } from "sonner";

const CHILD_COLORS = ["emma", "noah", "peach", "olive"];

export default function EditChildModal({ child, open, onClose, onSaved }) {
  const [draft, setDraft] = useState(child || {});
  const [saving, setSaving] = useState(false);

  useEffect(() => { setDraft(child || {}); }, [child]);

  if (!open || !child) return null;

  const handleSave = async (e) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    try {
      const updated = await updateChild(child.id, {
        name: draft.name,
        grade: draft.grade,
        school: draft.school || "",
        notes: draft.notes || "",
        color: draft.color || "emma",
        activities: draft.activities || [],
      });
      toast.success(`${updated.name} updated.`);
      onSaved?.(updated);
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save changes.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      data-testid={CHILDREN.editModal}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <form
        onSubmit={handleSave}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-lg bg-white rounded-2xl p-7 shadow-2xl space-y-5"
      >
        <div className="flex items-start justify-between">
          <div>
            <p className="label-overline">Edit child</p>
            <h2 className="font-display text-2xl text-[var(--fcc-ink)] mt-1">{child.name}</h2>
          </div>
          <button
            type="button"
            data-testid={CHILDREN.editCancelBtn}
            onClick={onClose}
            className="text-[var(--fcc-muted)] hover:text-[var(--fcc-ink)] p-1"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-3">
          <label className="block">
            <span className="label-overline">Name</span>
            <input
              data-testid={CHILDREN.editNameInput}
              required
              className="fcc-input w-full mt-1"
              value={draft.name || ""}
              onChange={(e) => setDraft({ ...draft, name: e.target.value })}
            />
          </label>
          <label className="block">
            <span className="label-overline">Grade</span>
            <input
              data-testid={CHILDREN.editGradeInput}
              className="fcc-input w-full mt-1"
              value={draft.grade || ""}
              onChange={(e) => setDraft({ ...draft, grade: e.target.value })}
            />
          </label>
          <label className="block">
            <span className="label-overline">School</span>
            <input
              data-testid={CHILDREN.editSchoolInput}
              className="fcc-input w-full mt-1"
              value={draft.school || ""}
              onChange={(e) => setDraft({ ...draft, school: e.target.value })}
            />
          </label>
          <label className="block">
            <span className="label-overline">Notes (optional)</span>
            <textarea
              data-testid={CHILDREN.editNotesInput}
              className="fcc-input w-full mt-1 min-h-[80px]"
              value={draft.notes || ""}
              onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
            />
          </label>
          <div>
            <span className="label-overline">Color tag</span>
            <div className="flex gap-2 mt-2">
              {CHILD_COLORS.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setDraft({ ...draft, color: c })}
                  className={`px-3 py-1.5 rounded-lg text-sm border transition ${
                    draft.color === c
                      ? "bg-[var(--fcc-ink)] text-white border-[var(--fcc-ink)]"
                      : "bg-white text-[var(--fcc-ink)] border-[var(--fcc-border)] hover:bg-[var(--fcc-cream-2)]"
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2.5 rounded-xl bg-white border border-[var(--fcc-border)] text-[var(--fcc-ink)] hover:bg-[var(--fcc-cream-2)] text-sm font-semibold"
          >
            Cancel
          </button>
          <button
            type="submit"
            data-testid={CHILDREN.editSaveBtn}
            disabled={saving}
            className="fcc-btn-primary disabled:opacity-60"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save changes
          </button>
        </div>
      </form>
    </div>
  );
}
